import type { LucideIcon } from 'lucide-react'
import { Plus } from 'lucide-react'
import type { ReactNode } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'

export type PickChoice = { id: string; title: string; detail: string; cover: string | null }

// Where something new goes when there's more than one place it could: which album the photos join, which book the
// recipe goes in. Each place is one tap; the last row starts a new one instead.
export function PickOne({
  open,
  onOpenChange,
  title,
  description,
  choices,
  icon: Icon,
  onPick,
  fresh,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  choices: PickChoice[]
  icon: LucideIcon
  onPick: (id: string) => void
  fresh?: { label: string; onSelect: () => void }
  // Anything to say under the choices, such as why a pick didn't go through.
  children?: ReactNode
}) {
  const row =
    'flex min-h-14 w-full items-center gap-3 rounded-2xl px-2 py-1.5 text-left outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50 active:bg-secondary'
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85svh] overflow-y-auto sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <ul className="-mx-2 flex flex-col gap-0.5" aria-label={title}>
          {choices.map((x) => (
            <li key={x.id}>
              <button type="button" className={row} onClick={() => onPick(x.id)}>
                {x.cover ? (
                  <img src={x.cover} alt="" className="size-11 shrink-0 rounded-xl object-cover" />
                ) : (
                  <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-secondary">
                    <Icon className="size-5 text-muted-foreground" aria-hidden />
                  </span>
                )}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-medium">{x.title}</span>
                  <span className="truncate text-sub text-muted-foreground">{x.detail}</span>
                </span>
              </button>
            </li>
          ))}
          {fresh && (
            <li>
              <button type="button" className={row} onClick={fresh.onSelect}>
                <span className="grid size-11 shrink-0 place-items-center rounded-xl border border-dashed border-input">
                  <Plus className="size-5" aria-hidden />
                </span>
                <span className="font-medium">{fresh.label}</span>
              </button>
            </li>
          )}
        </ul>
        {children}
      </DialogContent>
    </Dialog>
  )
}
